import { useSyncExternalStore } from 'react'

import { AuthService } from 'services/AuthService'
import { webLogger } from 'utils/logger'

import type { AppAuthUser } from 'services/AuthService'

type AuthSnapshot = {
  user: AppAuthUser | undefined
  loading: boolean
}

type AuthListener = () => void

const authListeners = new Set<AuthListener>()

let authSnapshot: AuthSnapshot = {
  user: undefined,
  loading: true,
}

let hasStartedSubscription = false

const emitAuthStoreChange = () => {
  for (const listener of Array.from(authListeners)) {
    listener()
  }
}

const setAuthSnapshot = (nextSnapshot: AuthSnapshot) => {
  if (authSnapshot.user === nextSnapshot.user && authSnapshot.loading === nextSnapshot.loading) {
    return
  }

  authSnapshot = nextSnapshot
  emitAuthStoreChange()
}

const startAuthSubscription = () => {
  if (hasStartedSubscription) {
    return
  }

  hasStartedSubscription = true

  // Suppress subscribe events until the initial auth state is resolved.
  // onIdTokenChanged fires with null before authStateReady() completes,
  // which would cause ProtectedRoute to redirect before the real state is known.
  let initialResolved = false

  void (async () => {
    try {
      const user = await AuthService.getCurrentUser()
      initialResolved = true
      setAuthSnapshot({ user, loading: false })
    } catch (error: unknown) {
      webLogger.error('Failed to bootstrap session store', { error })
      initialResolved = true
      setAuthSnapshot({ user: undefined, loading: false })
    }
  })()

  AuthService.subscribe(user => {
    if (!initialResolved) {
      return
    }
    setAuthSnapshot({ user, loading: false })
  })
}

const subscribe = (listener: AuthListener) => {
  authListeners.add(listener)
  startAuthSubscription()

  return () => {
    authListeners.delete(listener)
  }
}

const getSnapshot = () => {
  startAuthSubscription()
  return authSnapshot
}

const getServerSnapshot = () => authSnapshot

export const useAuthSessionSnapshot = () => {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
